<script lang="ts">
  /**
   * PUBLIC keyword audit — the lead magnet. No session, no token, no sign-up wall.
   *
   * IT STREAMS, and that is the whole design of this page. The work takes ten to
   * twenty seconds; a spinner for that long tells the visitor nothing and then dumps
   * everything at once with nowhere to start reading. So the server sends the keyword
   * list first — the table renders its rows immediately, greyed — and each row fills in
   * as its ranking lands. Nothing is faster. It just stops being invisible, and a
   * visitor watching their own competitors appear one by one does not leave.
   *
   * The stream's state and the tables live in `lib/audit` and `lib/ui/audit`, shared
   * with the admin Discovery page; this page is the form, the stepper and the footer.
   */
  import { previewCommunity, streamAudit } from '../lib/audit';
  import { AuditRun } from '../lib/audit/run.svelte';
  import RankingTable from '../lib/ui/audit/RankingTable.svelte';
  import LanguageSelect from '../lib/ui/audit/LanguageSelect.svelte';
  import KeywordImprove from '../lib/ui/audit/KeywordImprove.svelte';
  import SkoolPost from '@ui/SkoolPost.svelte';
  import type { PostAuthor } from '@ui/member';

  // Prefill, in two layers that must not be confused.
  //
  // `?url=` / `?keywords=` work EVERYWHERE and are a real feature: a prefilled link is
  // shareable, so an audit can be sent to a specific owner ready to run.
  //
  // The dev default is a TESTING convenience and is gated on `import.meta.env.DEV`,
  // which is true under the vite dev server and false in a production build. Without
  // that gate every public visitor would find someone else's community typed into the
  // form — the page's whole job is to ask for THEIRS.
  const params = new URLSearchParams(window.location.search);

  const DEV_URL = 'https://www.skool.com/sideguideapp-7183';

  // The Skoolers post that launched this tool, where the footer sends readers to say
  // what they think. A SNAPSHOT, not a live read: fetching the post on every page view
  // would put a Skool request behind a public page. The like and comment counts are as
  // of 14 September 2026 and will lag; the age is computed from the real date, so it
  // stays true.
  const FEEDBACK_POST = {
    url: 'https://www.skool.com/skoolers/free-fix-your-keywords-for-skool-discovery-app',
    title: 'Free `Fix Your Keywords` for Skool Discovery App',
    body:
      'I got inspired by a tool shared here last week for letting AI roast your about page and I built this:\n\n' +
      'This is a tool with (very little) AI that allows you to monitor how your skool is performing in Skool Discovery.',
    createdAt: '2026-09-14T15:38:18Z',
    category: 'Discussion',
    upvotes: 12,
    comments: 13,
    image: {
      url: 'https://assets.skool.com/f/e44528d24bfe4d65b0a412441feaa489/81cb9936f34d460dbaa494c6b8f69df673ffb9e37f5c4c2db44bdcbacaba0248-md.png',
      width: 720,
      height: 351,
    },
    author: {
      // Null, though the type says number: `<SkoolPost>` mounts the member hover card
      // only when this is set, and that card calls an authenticated admin endpoint that
      // a public page must never reach.
      member_id: null as unknown as number,
      external_id: '',
      handle: 'xconstantin-xconstantin-7658',
      name: 'Constantin C.',
      picture_profile:
        'https://assets.skool.com/f/1f9f30d6cd1e4e6792b78c545a5b116d/110e6df6c9db45ef9faf0317a935da4481c21f2956e146ca825daf949fc2c267-sm.jpg',
    } satisfies PostAuthor,
  };

  function openFeedbackPost(): void {
    window.open(FEEDBACK_POST.url, '_blank', 'noopener,noreferrer');
  }

  let url = $state(params.get('url') ?? (import.meta.env.DEV ? DEV_URL : ''));
  const run = new AuditRun();
  // The form is a step, not a permanent header: once it is submitted the answer is
  // what the page is about, and leaving the inputs up competes with it for attention.
  let submitted = $state(false);

  const canSubmit = $derived(url.trim().length > 0 && !run.busy);

  // The stepper reads existing state rather than adding its own counter. A second
  // source of truth for "where am I" is how a wizard ends up showing step 2 while
  // rendering step 3 — the step IS whether the form is submitted and whether the
  // improvements were asked for.
  // Step three replaces the ranking table rather than appending to it. One screen per
  // stage: the reader has finished reading where they stand and is now choosing what
  // to change, and leaving the previous stage on the page makes the choice compete
  // with a table nobody is reading any more.
  let showImprove = $state(false);
  const step = $derived(!submitted ? 1 : !showImprove ? 2 : 3);

  // `KeywordImprove` mounts fresh each time this flips on, so every visit to step three
  // starts from empty picks and asks for a new suggestion.
  function openImprove(): void {
    showImprove = true;
  }

  // A step is reachable once the work behind it exists: step 3 needs a finished,
  // saved audit, so it stays inert while one is still streaming.
  const canReach = $derived([false, true, submitted, run.recs !== null && run.auditId !== null]);

  function goStep(n: number): void {
    if (!canReach[n]) return;
    if (n === 1) {
      submitted = false;
      showImprove = false;
    } else if (n === 2) showImprove = false;
    else openImprove();
  }

  // null means "whatever the community itself is indexed as". The server resolves it for
  // the run; `detected` is the same answer fetched early, so the picker can say German
  // before anything is measured.
  let language = $state<string | null>(null);
  let detected = $state<string | null>(null);

  // Look the link up once it stops changing. Debounced because this fires while somebody
  // is still typing, and aborted on the next edit so a slow lookup for a half-typed URL
  // cannot land after the real one and overwrite it.
  $effect(() => {
    const typed = url.trim();
    if (!typed) {
      detected = null;
      return;
    }
    const abort = new AbortController();
    const timer = setTimeout(() => {
      void previewCommunity(typed, abort.signal).then((found) => {
        // Only the community's OWN language. A community the index has never seen has
        // none, and guessing one would put a finding in front of the reader that the
        // audit is about to contradict.
        detected = found?.language ?? null;
      });
    }, 400);
    return () => {
      clearTimeout(timer);
      abort.abort();
    };
  });

  async function submit(e: SubmitEvent): Promise<void> {
    e.preventDefault();
    if (!canSubmit) return;
    submitted = true;
    showImprove = false;
    await run.run((h) => streamAudit(url.trim(), [], h, language));
  }
</script>

<svelte:head>
  <title>Skool keyword audit — Sideguide</title>
</svelte:head>

  {#snippet improveButton()}
    <button class="btn btn-primary w-full sm:w-auto" onclick={openImprove}>
      Let’s improve this
    </button>
  {/snippet}

<div class="min-h-screen bg-base-200">
  <!-- Rendered under each step's own title rather than pinned to the top. It is
       orientation, not navigation: a reader looks at it when they arrive somewhere new,
       which is exactly when a title is being read. -->
  {#snippet steps()}
    <div class="flex w-full justify-center">
      <ul class="steps steps-horizontal overflow-visible text-xs">
        <!-- Two words each. They were a long form and a mobile short form swapped by
             breakpoint; at this length one label works everywhere, and the pair is gone
             with the divergence it invited. -->
        {#each ['Your link', 'Your rank', 'New keywords'] as label, i (label)}
          {@const n = i + 1}
          <li class="step min-w-24 sm:min-w-40" class:step-primary={step >= n}>
            {#if canReach[n] && n !== step}
              <button class="link link-hover text-[11px] sm:text-xs" onclick={() => goStep(n)}>
                {label}
              </button>
            {:else}
              <span class="text-[11px] sm:text-xs" class:font-semibold={n === step}>
                {label}
              </span>
            {/if}
          </li>
        {/each}
      </ul>
    </div>
  {/snippet}

  <div class="mx-auto px-4 py-8 sm:py-12" class:max-w-5xl={!showImprove}
       class:max-w-[1700px]={showImprove}>
    {#if !submitted}
      <!-- Narrower than the rest of the page on purpose. Stage one has one job and one
           control; a full-width column around a single field reads as an empty page
           with a field in it, and the eye has nowhere to land. -->
      <!-- Centred in the first screen. A single card parked in the top third of a
           phone reads as a page that failed to load the rest; centring it says this
           IS the page. -->
      <div class="mx-auto flex min-h-[60vh] max-w-2xl flex-col justify-center py-6 sm:py-12">
        <header class="mb-6 flex flex-col items-center text-center">
          <h1 class="font-display text-3xl font-bold sm:text-4xl">
            Where does your <!-- letters split so each can take its own colour; the label
            keeps it one word to a screen reader -->
            <span class="skool-word" aria-label="Skool"
              ><span>S</span><span>k</span><span>o</span><span>o</span><span>l</span></span
            > rank?
          </h1>
          <div class="mt-4">{@render steps()}</div>
        </header>

        <div class="rounded-box border border-base-300 bg-base-100 p-4 sm:p-6">
          <form class="flex flex-col gap-3 sm:flex-row" onsubmit={submit}>
            <!-- Height comes from `--size`, not `h-14`. daisyUI's `.input` sets
                 `height: var(--size)`, which only a size class defines — with none the
                 var is invalid, height falls back to `auto`, and it beats the utility.
                 The field rendered 22px tall beside a 60px button.

                 `min-h-*` rather than `h-*` or an arbitrary `--size`: used height is
                 max(height, min-height), so this holds whatever `height` resolves to
                 and needs no `!important` fight with the component class.
                 `input-bordered` went too — daisyUI 5 borders inputs by default. -->
            <input
              class="input min-h-14 w-full flex-1 text-base sm:min-h-16 sm:text-lg"
              type="text"
              placeholder="skool.com/your-community"
              aria-label="Your Skool community link"
              bind:value={url}
              disabled={run.busy}
            />
            <button
              class="btn btn-primary h-14 w-full px-8 text-base sm:h-16 sm:w-auto sm:px-10
                     sm:text-lg"
              type="submit"
              disabled={!canSubmit}
            >
              {run.busy ? 'Analysing…' : 'Run the audit'}
            </button>
          </form>
          <!-- Under the form and centred on it: the language is a refinement of a
               question the reader has usually already answered by pasting their link,
               so it sits with the field rather than pulling to one side. -->
          <div class="mt-3 flex justify-center">
            <LanguageSelect
              bind:value={language}
              resolved={run.community?.language ?? detected}
              disabled={run.busy}
            />
          </div>

          {#if run.error}
            <div class="alert alert-error mt-3"><span>{run.error}</span></div>
          {/if}
        </div>
      </div>
    {:else if run.error}
      <div class="alert alert-error">
        <span>{run.error}</span>
        <button class="btn btn-sm" onclick={() => (submitted = false)}>Change details</button>
      </div>
    {/if}

    {#if run.started && !showImprove}
      <section class="mt-8">
        <!-- One line, not three. This was a 40px logo over a two-line name-and-count
             block with the action beside it, then the steps, then a progress bar — four
             stacked things before a single row of data, and on a phone that is most of
             a screen spent before the answer. Identity is a caption here: the reader
             pasted this link ten seconds ago and needs confirming, not introducing. -->
        <div class="mb-3 flex flex-wrap items-center gap-3">
          {#if run.community?.logo_url}
            <!-- privacy-ok: the visitor's own community, which they just pasted -->
            <img
              src={run.community.logo_url}
              alt=""
              class="h-8 w-8 shrink-0 rounded-full object-cover ring-1 ring-base-300"
            />
          {/if}
          <h2 class="font-display text-base font-bold leading-tight sm:text-lg">
            {run.community?.display_name ?? 'Reading your community…'}
          </h2>
          {#if run.community}
            <span class="text-sm text-base-content/50">
              {run.community.total_members.toLocaleString()} members
            </span>
          {/if}
          {#if run.allRowsIn}
            <!-- On a wide screen the action sits in the identity row. On a phone the
                 same button renders AFTER the table instead (see below), so every step
                 ends with its action rather than opening with one. One snippet, two
                 placements — never two buttons to keep in step. -->
            <div class="hidden sm:ml-auto sm:block">{@render improveButton()}</div>
          {/if}
        </div>

        <div class="mb-4">{@render steps()}</div>

        <RankingTable {run} />

        {#if run.allRowsIn}
          <!-- The phone placement: every step ends with its action. -->
          <div class="mt-4 sm:hidden">{@render improveButton()}</div>
        {/if}

      </section>
    {/if}

    {#if showImprove}
      <section class="mt-8">
        <h2 class="mb-6 text-center font-display text-2xl font-bold">
          Choose your keywords
        </h2>

        <div class="mb-6">{@render steps()}</div>

        <KeywordImprove {run} {url} />
      </section>
    {/if}


  </div>

  <!-- Hidden on stage one, where the field is the only thing on screen. The one link
       here leads back INTO Skool, to the post this page was shared in: Skool groups
       reject posts that link out, so nothing on the page points anywhere else. -->
  {#if submitted}
    <footer class="mt-16 border-t hair">
      <div class="mx-auto max-w-xl px-4 pb-8 pt-12">
        <p class="text-center font-display text-xl font-bold">Did you like the tool?</p>
        <p class="mt-1 text-center text-sm text-base-content/60">
          I'd love to know what you think of it!
        </p>
        <div class="mt-6">
          <!-- privacy-ok: our own public Skool post, handed to <SkoolPost>, which marks the body it renders -->
          <SkoolPost
            author={FEEDBACK_POST.author}
            title={FEEDBACK_POST.title}
            body={FEEDBACK_POST.body}
            images={[FEEDBACK_POST.image]}
            mediaSide
            clamp={2}
            skoolUrl={FEEDBACK_POST.url}
            upvotes={FEEDBACK_POST.upvotes}
            commentCount={FEEDBACK_POST.comments}
            createdAt={FEEDBACK_POST.createdAt}
            category={FEEDBACK_POST.category}
            onclick={openFeedbackPost}
          />
        </div>
        <p class="mt-8 text-center text-xs text-base-content/40">Powered by Sideguide</p>
      </div>
    </footer>
  {/if}
</div>
